import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import { omit, invert, flatten } from 'lodash';
import { Button, Card, Grid, Space, Statistic, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { faUpDownLeftRight } from '@fortawesome/free-solid-svg-icons';
import FaIcon from '../../components/FaIcon';
import API from '../../api';
import { Formatter } from '../../utils';
import { dateRangeMap, parseDateRangeString } from '../Filters/types';
import { getDefaults, shouldFetchData } from './utils';
import { BarGraph, LineGraph, MixedGraph, Pie, StatsTable, WidgetType } from './types';
import './styles.scoped.css';

const dateRangeMapInverse = invert(dateRangeMap);
// const timezone = getLocalTimezone(); // get current timezone name
// const offset = timezone ? timeZonesMap[timezone].utc : timezone; // get current offset

interface WidgetProps {
  title: string | JSX.Element;
  sums: string[];
  groups: string[];
  type: WidgetType;
  apiParams: {
    dateRange: string;
    advertiserId: string;
  };
  config: {
    params: any;
    graphData: any;
  };
  onLoading?: () => any;
  mode: 'view' | 'preview' | 'edit';
  onRemove?: () => void;
  onTitleChange?: (title: string) => any;
  hideYAxisTicks?: boolean;
}

function Widget(props: WidgetProps) {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const {
    title,
    sums = [],
    groups = [],
    mode = 'view',
    onRemove,
    onTitleChange = () => {},
    apiParams,
    config,
    hideYAxisTicks = true,
    type,
  } = props;

  const defaults = useMemo(() => getDefaults(type), [type]);

  const [api, { loading }] = API.Stats.Custom();
  const [data, setData] = useState<any[]>([]);
  const [totalData, setTotalData] = useState([]);

  const shouldFetch = useCallback(() => {
    return shouldFetchData(type, sums, groups);
  }, [groups, sums, type]);

  const className = useMemo(() => {
    return classnames('widget', `widget-${mode}`);
  }, [mode]);

  const params = useMemo(() => {
    let sortBy;
    if (type === 'PIE_CHART') [sortBy] = sums;
    const updatedParams = {
      sortBy,
      limit: defaults.dataLimit,
      sortOrder: 'desc',
      sums,
      groups: flatten(groups.map((g) => g.split(':'))),
      ...apiParams,
      ...(config?.params ?? {}),
      ...(apiParams.dateRange && parseDateRangeString(apiParams.dateRange)),
    };
    return omit(updatedParams, 'dateRange');
  }, [config, defaults.dataLimit, apiParams, groups, sums, type]);

  const fetchData = useCallback(() => {
    if (!shouldFetch()) return;
    api({ params: { ...params } }).then(async (res: any) => {
      const { data: apiData } = res;
      let newTotalData = [];

      if (type === 'PIE_CHART') {
        const result = await api({
          params: { ...omit(params, 'groups'), groups: ['advertiserId'], limit: 1 },
        });
        newTotalData = result?.data?.items ?? [];
      }
      setData(apiData.items);
      setTotalData(newTotalData);
    });
  }, [api, params, shouldFetch, type]);

  useEffect(() => {
    // debounce(() => fetchData(), 500, { leading: true });
    fetchData();
  }, [fetchData]);

  const closeButton = useCallback(() => {
    if (mode !== 'edit') return null;
    return (
      <Button
        size="small"
        danger
        type="primary"
        icon={<CloseOutlined />}
        shape="circle"
        onClick={onRemove}
      />
    );
  }, [mode, onRemove]);

  const dragButton = useCallback(() => {
    if (mode !== 'edit') return null;
    return <Button icon={<FaIcon icon={faUpDownLeftRight} />} />;
  }, [mode]);

  // const configButton = useCallback(() => {
  //   if (process.env.REACT_APP_ENV === 'production' || mode !== 'edit') return null;

  //   const updatedFilters = { ...apiParams, dateRange: dateRangeMapInverse[apiParams.dateRange] };

  //   const d = (
  //     <pre>{JSON.stringify({ type, title, sums, groups, updatedFilters, config }, null, 2)}</pre>
  //   );

  //   return (
  //     <Tooltip
  //       title={d}
  //       placement="rightTop"
  //       trigger="click"
  //       overlayStyle={{ maxHeight: 250, overflow: 'auto' }}
  //       getPopupContainer={(node: any) => node.closest('.react-grid-layout') || <></>}
  //     >
  //       <SettingOutlined style={{ fontSize: 17, verticalAlign: 'middle' }} />
  //     </Tooltip>
  //   );
  // }, [config, apiParams, groups, mode, sums, title, type]);

  const getComponentByType = useCallback(
    (wType: WidgetType) => {
      switch (wType) {
        case 'STATS_VALUE': {
          const value = Formatter.count(data[0]?.[sums[0]]) || 0;
          return <Statistic value={value} />;
        }
        case 'BAR_GRAPH':
          return (
            <BarGraph
              data={data}
              sums={sums}
              groups={groups}
              dateRange={[params.fromDate, params.toDate]}
            />
          );
        case 'LINE_GRAPH':
          return (
            <LineGraph
              data={data}
              sums={sums}
              groups={groups}
              dateRange={[params.fromDate, params.toDate]}
              hideYAxisTicks={hideYAxisTicks}
            />
          );
        case 'MIXED_GRAPH':
          return (
            <MixedGraph
              data={data}
              sums={sums}
              groups={groups}
              dateRange={[params.fromDate, params.toDate]}
              graphConfig={props.config.graphData}
            />
          );
        case 'PIE_CHART':
          return <Pie data={data} totalData={totalData[0]} sums={sums} groups={groups} />;
        case 'STATS_TABLE':
          return <StatsTable data={data} columns={[...sums, ...groups]} loading={loading} />;
        default:
          return 'Invalid Component Type';
      }
    },
    [data, groups, hideYAxisTicks, loading, params, props?.config?.graphData, sums, totalData],
  );

  const showInitialText = mode === 'preview' && !shouldFetch();

  if (!defaults.wrapInCard) {
    return <div className="widget-wrapper">{getComponentByType(type)}</div>;
  }
  const showingComponent = !((defaults.useCardLoading && loading) || showInitialText);

  return (
    <Card
      className={className}
      title={
        <Typography.Text
          // level={3}
          editable={
            mode === 'edit'
              ? {
                onChange: (newTitle) => onTitleChange(newTitle),
                maxLength: 50,
                autoSize: { maxRows: 1 },
              }
              : false
          }
          style={{
            display: 'inline-block',
            margin: 0,
            minWidth: 0,
            maxWidth: isMobile ? 140 : 260,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </Typography.Text>
      }
      style={{ height: 390, width: '100%', marginBottom: 0 }}
      bodyStyle={showingComponent ? defaults.cardProps.bodyStyle : { height: 334, adding: 0 }}
      extra={
        <Space size={isMobile ? 'small' : 'middle'}>
          {!isMobile ? dateRangeMapInverse[apiParams.dateRange] : null}
          {/* {configButton()} */}
          {dragButton()}
          {closeButton()}
        </Space>
      }
      {...omit(defaults.cardProps, 'bodyStyle')}
    >
      {(() => {
        if (showInitialText) {
          return (
            <Typography.Title level={4} type="secondary" style={{ textAlign: 'center' }}>
              Select some metrics to appear here
            </Typography.Title>
          );
        }
        return data && getComponentByType(type);
      })()}
    </Card>
  );
}

export default Widget;
